import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Typography } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Home from './pages/Home';
import CareersPage from './pages/CareersPage';
import AssessmentFlow from './pages/AssessmentFlow';
import ResultsPage from './pages/ResultsPage';
import SharedResultsPage from './pages/SharedResultsPage';
import TestResults from './pages/TestResults';
import AdminPage from './pages/AdminPage';
// Auth pages
import AthleteLoginPage from './pages/auth/AthleteLoginPage';
import AthleteRegisterPage from './pages/auth/AthleteRegisterPage';
import CompanyLoginPage from './pages/auth/CompanyLoginPage';
import CompanyRegisterPage from './pages/auth/CompanyRegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
// Profile pages
import ProfileCreatePage from './pages/profile/ProfileCreatePage';
import ProfileViewPage from './pages/profile/ProfileViewPage';
import ProfileSettingsPage from './pages/profile/ProfileSettingsPage';
// Athlete pages
import AthleteDashboardPage from './pages/athlete/AthleteDashboardPage';
import ScoutInboxPage from './pages/athlete/ScoutInboxPage';
import ScoutThreadPage from './pages/athlete/ScoutThreadPage';
// Company pages
import CompanyDashboardPage from './pages/company/CompanyDashboardPage';
import ProfileSearchPage from './pages/company/ProfileSearchPage';
import ProfileDetailPage from './pages/company/ProfileDetailPage';
import ScoutSentPage from './pages/company/ScoutSentPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0066cc', // 厚労省サイトに近い青色
    },
    secondary: {
      main: '#ff9800',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      '"Hiragino Kaku Gothic ProN"',
      '"Hiragino Sans"',
      'Meiryo',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
    fontSize: 15,
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.8,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.7,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // プロフィール作成中はステップ間で上に戻さない
    if (pathname === '/profile/create') return;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <BrowserRouter>
            <ScrollToTop />
            <Box sx={{ flex: 1 }}>
              <Routes>
                {/* 既存ルート */}
                <Route path="/" element={<Home />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/assessment" element={<AssessmentFlow />} />
                <Route path="/results" element={<ResultsPage />} />
                <Route path="/results/shared/:id" element={<SharedResultsPage />} />
                <Route path="/test-results" element={<TestResults />} />
                <Route path="/admin" element={<AdminPage />} />

                {/* 認証ルート */}
                <Route path="/auth/athlete-login" element={<AthleteLoginPage />} />
                <Route path="/auth/athlete-register" element={<AthleteRegisterPage />} />
                <Route path="/auth/company-login" element={<CompanyLoginPage />} />
                <Route path="/auth/company-register" element={<CompanyRegisterPage />} />
                <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />

                {/* プロフィール作成（アスリートのみ） */}
                <Route path="/profile/create" element={
                  <ProtectedRoute requiredType="athlete"><ProfileCreatePage /></ProtectedRoute>
                } />
                <Route path="/profile/view" element={
                  <ProtectedRoute requiredType="athlete"><ProfileViewPage /></ProtectedRoute>
                } />
                <Route path="/profile/settings" element={
                  <ProtectedRoute requiredType="athlete"><ProfileSettingsPage /></ProtectedRoute>
                } />

                {/* アスリートダッシュボード */}
                <Route path="/athlete/dashboard" element={
                  <ProtectedRoute requiredType="athlete"><AthleteDashboardPage /></ProtectedRoute>
                } />
                <Route path="/athlete/scouts" element={
                  <ProtectedRoute requiredType="athlete"><ScoutInboxPage /></ProtectedRoute>
                } />
                <Route path="/athlete/scouts/:threadId" element={
                  <ProtectedRoute requiredType="athlete"><ScoutThreadPage /></ProtectedRoute>
                } />

                {/* 企業ダッシュボード */}
                <Route path="/company/dashboard" element={
                  <ProtectedRoute requiredType="company"><CompanyDashboardPage /></ProtectedRoute>
                } />
                <Route path="/company/search" element={
                  <ProtectedRoute requiredType="company"><ProfileSearchPage /></ProtectedRoute>
                } />
                <Route path="/company/profile/:uuid" element={
                  <ProtectedRoute requiredType="company"><ProfileDetailPage /></ProtectedRoute>
                } />
                <Route path="/company/scouts" element={
                  <ProtectedRoute requiredType="company"><ScoutSentPage /></ProtectedRoute>
                } />

                {/* 404 */}
                <Route path="*" element={
                  <Box sx={{ textAlign: 'center', py: 10 }}>
                    <Typography variant="h4" sx={{ mb: 2 }}>404</Typography>
                    <Typography variant="body1" sx={{ color: '#666' }}>ページが見つかりませんでした</Typography>
                  </Box>
                } />
              </Routes>
            </Box>
            <Box
              component="footer"
              sx={{
                py: 2,
                textAlign: 'center',
                bgcolor: '#333',
                color: '#fff',
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                &copy; Career Crea Co., Ltd. All Rights Reserved
              </Typography>
            </Box>
          </BrowserRouter>
        </Box>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
